/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * ASCENDING BY SIZE, NOT BY POPULARITY.
 *
 * SCAPI returns this group hit-count descending — measured on the running app,
 * both brands: NM search `dress` opens `Large(16) Medium(15) Small(14) …` and
 * BG PLP womens-clothing opens `2(8) 6(8) 8(8) 0(7) …`. No live source on
 * either brand orders a size list that way.
 *
 * Live BG's own order is the positive control this comparator is checked
 * against. Its expanded SIZE group reads, top to bottom:
 *
 *     0-3 Months · 1.5 · 2 · 3 · 3-6 Months · 4 · 5
 *
 * — which is ascending on the value's leading number, with the tie at 3
 * broken so the bare `3` precedes `3-6 Months`. That is exactly what this
 * comparator produces, and the unit test asserts the seven-row sequence.
 *
 * Live NM's order is NOT reproducible here and is deliberately not attempted.
 * Its list is an authored harmonisation — `XS, 0, 2` / `S, 4, 6` / `M, 8, 10`
 * / `Petite XS, 0, 2` / `Plus 1X, 14, 16` / `One Size`, then a raw tail of
 * `24 26 27 28 29 30 2.5 C`. That taxonomy is catalogue data we do not have,
 * and inventing it is a much larger job than fixing the control. Ascending is
 * applied to NM as the neutral: it matches live NM's harmonised block, which
 * IS in ascending size order, and it is strictly closer than hit-count
 * descending, which matches nothing on either brand.
 *
 * NUMERIC-PREFIXED VALUES SORT FIRST, then everything else alphabetically.
 * Live BG's list opens on numbers with the scroll thumb at the top of its
 * track, so numerics-first is measured rather than chosen. Where the two
 * candidate rules (plain lexicographic vs numeric-aware) would disagree —
 * `10` against `2` — live's captured seven rows contain no such pair, so the
 * capture does not discriminate. Numeric-aware is used because lexicographic
 * puts `10 12 14 16` ahead of `2` on our own catalogue, which is visibly
 * wrong, and because a comparator that degrades to something defensible when
 * the evidence runs out is worth more than one that reproduces a coin flip.
 */
const LEADING_NUMBER = /^(\d+(?:\.\d+)?)/;

/**
 * LETTER SIZES RANK BY SIZE, NOT ALPHABETICALLY — and that is measured, not
 * assumed. Live NM's harmonised block runs `XS, 0, 2` / `S, 4, 6` /
 * `M, 8, 10` / `L, 12, 14` / `XL, 16, 18` / `XXL, 20`, top to bottom: the
 * conventional smallest-to-largest ladder. Sorting these as text instead
 * yields `2X-Small, Large, Medium, Small, X-Large, X-Small`, which is a
 * different kind of wrong from the hit-count order it replaced rather than a
 * fix — our NM catalogue spells them out in full, so the alphabetical answer
 * is what a naive comparator produces on real data.
 *
 * Both the abbreviated and spelled-out spellings are here because the
 * catalogue ships both, sometimes in one result set. A spelling absent from
 * this table is NOT forced into the ladder — it falls through to the
 * alphabetical tier, so an unrecognised value is placed predictably instead
 * of being guessed at.
 */
const LETTER_SIZE_RANK: Readonly<Record<string, number>> = {
    '3XS': -3,
    XXXS: -3,
    '3X-SMALL': -3,
    'XXX-SMALL': -3,
    '2XS': -2,
    XXS: -2,
    '2X-SMALL': -2,
    'XX-SMALL': -2,
    XS: -1,
    'X-SMALL': -1,
    'EXTRA SMALL': -1,
    S: 0,
    SMALL: 0,
    M: 1,
    MEDIUM: 1,
    L: 2,
    LARGE: 2,
    XL: 3,
    'X-LARGE': 3,
    'EXTRA LARGE': 3,
    '2XL': 4,
    XXL: 4,
    '2X-LARGE': 4,
    'XX-LARGE': 4,
    '3XL': 5,
    XXXL: 5,
    '3X-LARGE': 5,
    'XXX-LARGE': 5,
};

/**
 * Three tiers, in this order: numeric sizes ascending, then the letter
 * ladder, then everything else alphabetically.
 *
 * Numerics lead because live BG's expanded group opens on them with its
 * scroll thumb at the top of its track. The letter ladder is checked FIRST
 * per value even though it is the second tier — `2X-Small` and `3XL` both
 * begin with a digit, and reading that digit as a size would scatter them
 * through the numeric run.
 */
function sizeTier(v: string): { tier: number; rank: number } {
    const key = v.trim().toUpperCase();
    const letter = LETTER_SIZE_RANK[key];
    if (letter !== undefined) return { tier: 1, rank: letter };
    const num = LEADING_NUMBER.exec(v.trim());
    if (num) return { tier: 0, rank: Number(num[1]) };
    return { tier: 2, rank: 0 };
}

export function compareSizeValues(a: string, b: string): number {
    const ta = sizeTier(a);
    const tb = sizeTier(b);
    if (ta.tier !== tb.tier) return ta.tier - tb.tier;
    if (ta.tier !== 2 && ta.rank !== tb.rank) return ta.rank - tb.rank;
    return a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' });
}
